import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { EDITABLE_HOMEPAGE_CAROUSEL_HEADINGS_FLAG } from 'shared/constants';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useDispensaryMenuSections from 'src/dispensary/hooks/use-dispensary-menu-sections';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useRecentlyViewedCarouselEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import useViewportVisibility from 'src/hooks/use-viewport-visibility';
import { PersonalizedProducts } from 'src/components/carousels/products-carousels/personalized-products-carousel';
import { CarouselTitleSlotProvider } from 'src/components/carousel-v2/carousel-title-slot-context';
import { getEnabledBanners } from 'src/components/menu-image-banner/menu-image-banner';
import {
  CarouselRenderStateProvider,
  useReportCarouselRenderState,
} from 'src/components/carousels/carousel-render-state';

import { CollectionCarousel as CollectionItemsSection } from 'src/components/carousels/products-carousels/collection-carousel';
import {
  BrandCarousel as BrandSection,
  CategoryCarousel as CategorySection,
  SpecialsCarousel as SpecialsSection,
  SubcategoryCarousel as SubcategorySection,
  StaffPicksCarousel as StaffPicksSection,
  TopSellersCarousel as TopSellersSection,
} from '../carousels/generic-carousels';

import { CustomCarousel as CustomSection } from '../carousels/custom-carousels';
import { SpecialOffersCarousel as OffersSection } from '../carousels/offers-carousels';
import { RecentlyViewedCarousel as RecentlyViewedSection } from '../carousels/products-carousels';
import { createCarouselSequence } from './helpers';
import ImageSection from '../carousels/custom-carousels/image-section';

export const HOMEPAGE_CAROUSEL_DEFERRAL_FLAG = 'growth.ecomm.defer-homepage-carousels.rollout';

// Mirrors PersonalizedProductsSectionRender in
// src/cms/menu-landing/blocks/personalized-carousels.tsx, the equivalent Puck CMS block.
function RecommendedForYouSection({ title }) {
  const { dispensary } = useDispensary();
  const { isLoggedIn } = useUser();
  const { personalizedProductsQuery } = useProductsForHomePage({ dispensaryId: dispensary?.id ?? '' });
  const products = personalizedProductsQuery.data?.getPersonalizedProducts?.products ?? [];
  useReportCarouselRenderState({
    loading: isLoggedIn && personalizedProductsQuery.loading,
    hasContent: isLoggedIn && products.length > 0,
  });

  return <PersonalizedProducts personalizedProductsQuery={personalizedProductsQuery} title={title} />;
}

const ALWAYS_RENDERED_SECTION_TYPES = new Set([
  'BRAND',
  'CATEGORY',
  'SUBCATEGORY',
  'STAFF_PICKS',
  'TOP_SELLERS',
  'CUSTOM',
  'OFFERS',
  'COLLECTION',
]);

function isCarouselSectionEligibleForRender({
  section,
  hideSpecials,
  showRecentlyViewedCarousel,
  isLoggedIn,
  isKiosk,
  isKioskProEnabled,
}) {
  const { sectionType } = section;

  if (sectionType === 'RECENTLY_VIEWED') {
    return showRecentlyViewedCarousel;
  }
  if (sectionType === 'RECOMMENDED_FOR_YOU') {
    return isLoggedIn && (!isKiosk || isKioskProEnabled);
  }
  if (sectionType === 'SPECIALS') {
    return !hideSpecials;
  }
  if (sectionType === 'IMAGE') {
    return Boolean(section.image && getEnabledBanners([section.image], isKiosk).length);
  }
  return ALWAYS_RENDERED_SECTION_TYPES.has(sectionType);
}

const CarouselSection = React.memo(
  ({ section, index, dispensaryState, hideSpecials, showRecentlyViewedCarousel, editableHeadingsEnabled }) => {
    const storedOverride = section.headingOverride?.trim();
    const headingOverride =
      editableHeadingsEnabled && storedOverride && section.label === storedOverride ? storedOverride : undefined;
    const displaySection = {
      ...section,
      label: headingOverride ?? section.legacyLabel ?? section.label,
    };

    switch (section.sectionType) {
      case 'BRAND':
        return <BrandSection key={section.id} section={displaySection} sectionIndex={index} />;
      case 'CATEGORY':
        return (
          <CategorySection
            key={section.id}
            section={displaySection}
            sectionIndex={index}
            dispensaryState={dispensaryState}
          />
        );
      case 'RECENTLY_VIEWED':
        return (
          showRecentlyViewedCarousel && (
            <RecentlyViewedSection key={section.id} section={displaySection} sectionIndex={index} />
          )
        );
      case 'RECOMMENDED_FOR_YOU':
        return <RecommendedForYouSection key={section.id} title={headingOverride} />;
      case 'SPECIALS':
        return (
          !hideSpecials && (
            <SpecialsSection key={section.id} section={displaySection} sectionIndex={index} title={headingOverride} />
          )
        );
      case 'SUBCATEGORY':
        return (
          <SubcategorySection
            key={section.id}
            section={displaySection}
            sectionIndex={index}
            dispensaryState={dispensaryState}
          />
        );
      case 'STAFF_PICKS':
        return (
          <StaffPicksSection key={section.id} section={displaySection} sectionIndex={index} title={headingOverride} />
        );
      case 'TOP_SELLERS':
        return (
          <TopSellersSection key={section.id} section={displaySection} sectionIndex={index} title={headingOverride} />
        );
      case 'CUSTOM':
        return <CustomSection key={section.id} section={section} sectionIndex={index} />;
      case 'OFFERS':
        return <OffersSection key={section.id} section={displaySection} title={headingOverride} />;
      case 'COLLECTION':
        return <CollectionItemsSection key={section.id} section={section} sectionIndex={index} />;
      case 'IMAGE':
        return <ImageSection key={section.id} section={section} />;
      default:
        return null;
    }
  }
);

CarouselSection.displayName = 'CarouselSection';

// The legacy bot override reads browser navigator and cannot agree with SSR. These wrappers defer
// every user agent so the server and first client render have the same child structure.
const DEFERRED_VIEWPORT_OPTIONS = { disableBotDetection: true, rootMargin: '300px 0px' };

const DeferredCarouselItem = ({ children }) => {
  const { ref, hasBeenVisible } = useViewportVisibility(DEFERRED_VIEWPORT_OPTIONS);
  const [renderState, setRenderState] = React.useState('loading');
  const shouldReserveSpace = !hasBeenVisible || renderState === 'loading';

  return (
    <CarouselRenderStateProvider value={setRenderState}>
      <DeferredSection ref={ref} $reserveSpace={shouldReserveSpace} data-testid='deferred-homepage-carousel-section'>
        {hasBeenVisible ? children : null}
      </DeferredSection>
    </CarouselRenderStateProvider>
  );
};

const DeferredUnsizedItem = ({ children, testId }) => {
  const { ref, hasBeenVisible } = useViewportVisibility(DEFERRED_VIEWPORT_OPTIONS);

  return (
    <DeferredSentinel ref={ref} data-testid={testId}>
      {hasBeenVisible ? children : null}
    </DeferredSentinel>
  );
};

export const HomepageCarouselSections = observer(() => {
  const { dispensary } = useDispensary();
  const router = useRouter();
  const dispensaryId = dispensary?.id;
  const { isLoggedIn } = useUser();
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const flags = useFlags();
  const editableHeadingsEnabled = flags[EDITABLE_HOMEPAGE_CAROUSEL_HEADINGS_FLAG] ?? false;
  // Freeze the SSR-bootstrapped rollout decision for this page lifetime. Applying a later
  // LaunchDarkly stream/identify update would remount the sequence after queries/layout began.
  // Missing/unavailable flags fail safely to the existing eager-render behavior.
  const carouselDeferralEnabled = React.useRef(flags[HOMEPAGE_CAROUSEL_DEFERRAL_FLAG] === true).current;

  const personalizationRecentlyViewedEnabled = useRecentlyViewedCarouselEnabled();
  const showRecentlyViewedCarousel = !isKiosk && personalizationRecentlyViewedEnabled && isLoggedIn;

  const hideSpecials = useHideDiscountFromConsumer(dispensary);

  const { menuSections } = useDispensaryMenuSections();
  const { iterables } = useCommerceComponents();
  const interstitials = iterables.StoreFrontCarouselInterstitials();
  const carouselSequence = createCarouselSequence(menuSections, interstitials);

  if (!dispensaryId) {
    return null;
  }

  const { hideTitle } = router?.query ?? {};
  const dispensaryState = dispensary.location?.state;

  return (
    <Sections data-testid='homepage-carousel-sections'>
      {carouselSequence.map((item, index) => {
        if (item.type === 'interstitial') {
          const Interstitial = item.interstitial;

          if (!carouselDeferralEnabled) {
            // eslint-disable-next-line react/no-array-index-key
            return <Interstitial key={`interstitial-${index}`} />;
          }

          return (
            // Remote interstitials are opaque and may intentionally render nothing. Keep the
            // viewport mount gate, but use an unsized sentinel so a null interstitial cannot
            // collapse a reserved slot and pull later carousels through the visibility margin.
            // eslint-disable-next-line react/no-array-index-key
            <DeferredUnsizedItem key={`interstitial-${index}`} testId='deferred-homepage-interstitial'>
              <Interstitial />
            </DeferredUnsizedItem>
          );
        }

        if (
          !isCarouselSectionEligibleForRender({
            section: item.carousel,
            hideSpecials,
            showRecentlyViewedCarousel,
            isLoggedIn,
            isKiosk,
            isKioskProEnabled,
          })
        ) {
          return null;
        }

        const sectionWithTitle = hideTitle ? { ...item.carousel, hideTitle } : item.carousel;

        const carousel = (
          <CarouselTitleSlotProvider key={sectionWithTitle.id} carouselId={sectionWithTitle.id}>
            <CarouselSection
              section={sectionWithTitle}
              index={index}
              dispensaryState={dispensaryState}
              hideSpecials={hideSpecials}
              showRecentlyViewedCarousel={showRecentlyViewedCarousel}
              editableHeadingsEnabled={editableHeadingsEnabled}
            />
          </CarouselTitleSlotProvider>
        );

        if (!carouselDeferralEnabled) {
          return carousel;
        }

        if (sectionWithTitle.sectionType === 'IMAGE') {
          return (
            <DeferredUnsizedItem key={sectionWithTitle.id} testId='deferred-homepage-image-section'>
              {carousel}
            </DeferredUnsizedItem>
          );
        }

        return <DeferredCarouselItem key={sectionWithTitle.id}>{carousel}</DeferredCarouselItem>;
      })}
    </Sections>
  );
});

const Sections = styled.div``;
const DeferredSentinel = styled.div``;

// Keep the slot reserved while the mounted carousel reports loading. The section's explicit
// render-state contract releases it for both natural-height content and settled-empty results,
// avoiding both the activation cascade and permanent blank gaps.
const DeferredSection = styled.div`
  ${({ $reserveSpace }) =>
    $reserveSpace &&
    `
      min-height: var(--dcm-homepage-carousel-placeholder-height, 420px);
    `}
`;
